
































import { Vue, Component, Prop, Watch } from "@/shared/vuepack";
import moment from "moment";
import _ from "lodash";
import PayrollMonthEdit from "./MonthEdit.vue";
import { FetchState } from "@/shared/types";
import { DateGenerationCode } from "../../types";
import { useBankHolidayStore } from "@/employer/store/bankHoliday";
import { usePayrollCalendarStore } from "@/employer/store/payrollCalendar";

@Component({components: { PayrollMonthEdit }})
export default class YearEdit extends Vue {

  selectedDaysByMonth: number[][] = null;
  bankHolidaysByMonth: number[][] = null;
  flaggedDaysByMonth: number[][] = null;

  @Prop({required: true})
  forEmployerId: number;

  @Prop({required: true})
  year: number;

  @Prop({required: false, default: null})
  lockEndDate: string;

  @Prop({required: false})
  firstEligibleDate: string;

  @Prop({required: false, type: Boolean, default: false})
  enablePriorYearNavigation: boolean;

  @Prop({required: false, type: Boolean, default: false})
  enableNextYearNavigation: boolean;

  @Prop({required: false, type: Boolean, default: false})
  disableAll: boolean;

  bankHolidayStore = useBankHolidayStore();
  payrollCalendarStore = usePayrollCalendarStore();

  created () {
    this.initPayrollDates();
    this.initBankHolidays();
  }

  get holidaysFetchState () {
    return this.bankHolidayStore.bankHolidaysInYearFetchState(this.year);
  }

  get datesFetchState () {
    return this.payrollCalendarStore.fetchStateForYear(this.year);
  }

  @Watch ("employerId")
  onEmployerIdChanged () {
    this.initPayrollDates();
  }

  @Watch ("year")
  onYearChanged () {
    this.initPayrollDates();
    this.initBankHolidays();
  }

  @Watch("payrollCalendarStore.isAnyYearDirty")
  onIsAnyYearDirtyChanges () {
    if (!this.payrollCalendarStore.isAnyYearDirty) {
      // we just reverted all changes or went round-trip to the DB
      this.initPayrollDates();
    }
  }

  initPayrollDates () {
    this.selectedDaysByMonth = null;
    this.flaggedDaysByMonth = null;
    this.payrollCalendarStore.getPayrollDates({employerId: this.forEmployerId, year: this.year});
    this.copyPayrollDates();
  }

  initBankHolidays () {
    this.bankHolidaysByMonth = null;
    this.bankHolidayStore.loadBankHolidays(this.year);
    this.copyBankHolidays();
  }

  @Watch ("holidaysFetchState")
  onHolidaysFetchStateChanged () {
    this.copyBankHolidays();
  }

  @Watch("datesFetchState")
  onDatesFetchStateChanged () {
    this.copyPayrollDates();
  }

  copyPayrollDates () {
    if (this.selectedDaysByMonth === null && this.datesFetchState === FetchState.Loaded) {
      this.setSelectedDatesFromServer();
      this.setFlaggedDatesFromServer();
    }
  }

  copyBankHolidays () {
    if (this.bankHolidaysByMonth === null && this.holidaysFetchState === FetchState.Loaded) {
      this.bankHolidaysByMonth = this.reduceDates(this.bankHolidayStore.bankHolidaysInYear(this.year));
    }
  }

  setSelectedDatesFromServer () {
    const dates = this.payrollCalendarStore.payrollDatesInYear(this.year).map(v => v.date);
    this.selectedDaysByMonth = this.reduceDates(dates);
  }

  setFlaggedDatesFromServer () {
    const dates = this.payrollCalendarStore.payrollDatesInYear(this.year)
      .filter(v => v.dateGenerationCode !== DateGenerationCode.None
                    && v.dateGenerationCode !== DateGenerationCode.ManuallyAdjusted)
      .map(v => v.date);
    this.flaggedDaysByMonth = this.reduceDates(dates);
  }

  reduceDates (dateStrings: string[]): number[][] {
    const accumulator: number[][] = [];
    for (let month = 0; month < 12; month++) {
      accumulator[month] = [];
    }
    for (const value of dateStrings) {
      const momentInstance = moment(value, "YYYY-MM-DD");
      accumulator[momentInstance.month()].push(momentInstance.date());
    }
    return accumulator;
  }

  get isLoaded () {
    return this.bankHolidaysByMonth !== null && this.selectedDaysByMonth !== null;
  }

  get months () {
    return _.range(this.renderFromMonth, 12);
  }

  get renderFromMonth () {
    return this.firstEligibleDate ? moment(this.firstEligibleDate).month() : 0;
  }

  get firstEditableDate () {
    if (!this.lockEndDate) { return this.firstEligibleDate; }

    return moment(this.lockEndDate).add(1, "day").toISOString();
  }

  getSelectedDaysForMonth (month: number) {
    return this.selectedDaysByMonth[month] || [];
  }

  getFlaggedDaysForMonth (month: number) {
    return this.flaggedDaysByMonth[month] || [];
  }

  getDisableForMonth (month: number) {
    const firstEditableMoment = moment(this.firstEditableDate);

    if (this.disableAll
      || this.year < firstEditableMoment.year()
      || (this.year === firstEditableMoment.year() && month < firstEditableMoment.month())
    ) {
      return { allInput: true };
    }

    if (this.year === firstEditableMoment.year() && month === firstEditableMoment.month()) {
      return { daysBefore: firstEditableMoment.date() };
    }

    return {};
  }

  selectedDaysChanged (month: number, data: number[]) {
    const clone = [...this.selectedDaysByMonth];
    clone[month] = data;
    this.selectedDaysByMonth = clone;
    this.payrollCalendarStore.applyDates({
      year: this.year,
      monthIndex: month,
      days: data
    });
  }

  navigateYear (year: number) {
    this.$emit("changeYear", year);
  }
}
